import { ChartData } from "chart.js";
import { CalculatorConfig } from "@/lib/country/types";
import { CHART_COLORS, CHART_FILLS } from "@/lib/constants/chartPalette";

// Re-export ChartData for convenience
export type { ChartData };

/**
 * Builds chart data based on the calculator results
 */
export function buildChartData(
  results: Record<string, any>,
  config: CalculatorConfig,
  inputs: Record<string, any>
): (ChartData<any> & { chartType?: "pie" | "doughnut" | "line" | "bar" }) | null {
  if (!results || Object.keys(results).length === 0) return null;

  // Pass-through pre-built chartData from calculation engine
  if (results.chartData) {
    return results.chartData;
  }

  if (config.id === "irs-penalty-relief-aep") {
    const labels = results.projection.map((d: any) => `Month ${d.month}`);
    const standardData = results.projection.map((d: any) => d.standardPenalty);
    const netOwedData = results.projection.map((d: any) => d.netOwed);
    return {
      labels,
      datasets: [
        {
          label: "Standard Accrued Penalty",
          data: standardData,
          borderColor: CHART_COLORS.danger,
          backgroundColor: "transparent",
          tension: 0.2,
        },
        {
          label: "Net Penalty Owed (with AEP)",
          data: netOwedData,
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_FILLS.primarySoft,
          tension: 0.2,
          fill: true,
        }
      ]
    };
  }

  if (config.id === "uk-bnpl-dpc-rights") {
    const labels = results.projection.map((d: any) => `Month ${d.month}`);
    const paidData = results.projection.map((d: any) => d.cumulativePaid);
    const balanceData = results.projection.map((d: any) => d.remainingBalance);
    return {
      labels,
      datasets: [
        {
          label: "Cumulative Paid",
          data: paidData,
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_FILLS.primarySoft,
          tension: 0.2,
          fill: true,
        },
        {
          label: "Remaining Balance",
          data: balanceData,
          borderColor: CHART_COLORS.secondary,
          backgroundColor: "transparent",
          tension: 0.2,
        }
      ]
    };
  }

  if (config.id === "new-home-gst-rebate") {
    return {
      labels: ["Federal housing rebate", "Ontario provincial rebate", "Ontario ONHAP"],
      datasets: [{
        label: "Housing-tax relief",
        data: [
          results.federalRebate ?? 0,
          results.enhancedOntario ?? 0,
          results.ontarioTopUp ?? 0,
        ],
        backgroundColor: [
          CHART_COLORS.primary,
          CHART_COLORS.secondary,
          CHART_COLORS.accentOrange,
        ],
      }],
      chartType: "bar",
    };
  }

  // Helper to create balance and contributions dataset
  const createBalanceContributionsDataset = (
    data: any[],
    labelKey: string,
    balanceKey: string,
    contributionsKey: string
  ) => {
    const labels = data.map((d: any) => d[labelKey]);
    const balanceData = data.map((d: any) => d[balanceKey]);
    const contributionsData = data.map((d: any) => d[contributionsKey]);

    return {
      labels,
      datasets: [
        {
          label: "Total Balance",
          data: balanceData,
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_FILLS.primarySoft,
          tension: 0.2,
          fill: true,
        },
        {
          label: "Total Contributions",
          data: contributionsData,
          borderColor: CHART_COLORS.secondary,
          backgroundColor: "transparent",
          tension: 0.2,
        },
      ],
    };
  };

  // Yearly breakdown projection
  if (results.yearlyBreakdown) {
    return createBalanceContributionsDataset(
      results.yearlyBreakdown.map((d: any) => ({ ...d, yearLabel: `Yr ${d.year}` })),
      "yearLabel",
      "balance",
      "contributions"
    );
  }

  // HBP Repayment Schedule
  if (results.useHbpRepaymentSchedule) {
    const labels = results.projection.map((d: any) => `Yr ${d.year}`);
    const repaidData = results.projection.map((d: any) => d.totalRepaid);
    const balanceData = results.projection.map((d: any) => d.remainingBalance);
    return {
      labels,
      datasets: [
        {
          label: "Total Repaid to RRSP",
          data: repaidData,
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_FILLS.primarySoft,
          tension: 0.2,
          fill: true,
        },
        {
          label: "Remaining HBP Balance",
          data: balanceData,
          borderColor: CHART_COLORS.danger,
          backgroundColor: CHART_FILLS.dangerSoft,
          tension: 0.2,
          fill: true,
        }
      ]
    };
  }

  // Overseas Student Loan Repayment line chart
  if (config.id === "overseas-student-loan-repayment-calculator" && results.projection) {
    const labels = results.projection.map((d: any) => `Yr ${d.year}`);
    const balanceData = results.projection.map((d: any) => d.balance);
    const repaymentData = results.projection.map((d: any) => d.repayment);
    return {
      labels,
      datasets: [
        {
          label: "Remaining Loan Balance",
          data: balanceData,
          borderColor: CHART_COLORS.danger,
          backgroundColor: CHART_FILLS.dangerSoft,
          tension: 0.2,
          fill: true,
        },
        {
          label: "Annual Repayment",
          data: repaymentData,
          borderColor: CHART_COLORS.primary,
          backgroundColor: "transparent",
          tension: 0.2,
        }
      ]
    };
  }

  // KVdR 9/10 Rule eligibility bar chart
  if (config.id === "kvdr-9-10-rule-eligibility-calculator") {
    return {
      labels: ["GKV Years Needed (90%)", "GKV Years Achieved (incl. child credit)"],
      datasets: [{
        label: "Years",
        data: [
          Math.round(results.requiredGkvYears || 0),
          Math.round(results.totalGkvYears || 0)
        ],
        backgroundColor: [CHART_COLORS.danger, CHART_COLORS.primary],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1,
      }]
    };
  }

  // Code wage add-back and the capped contribution basis, from live inputs.
  if (config.id === "new-labour-codes-ctc-restructuring-calculator") {
    return {
      labels: ["Included basic/DA", "Excluded amount added back", "Computed code wage", "PF contribution wage"],
      datasets: [{
        label: "Monthly amount (₹)",
        data: [
          Math.max(0, (results.codeWageBase ?? 0) - (results.allowanceAddBack ?? 0)),
          results.allowanceAddBack ?? 0,
          results.codeWageBase ?? 0,
          results.pfContributionWage ?? 0
        ],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.accentOrange, CHART_COLORS.primary, CHART_COLORS.blue],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1,
      }]
    };
  }

  if (config.id === "irs-mileage-rate-calculator-2026") {
    return {
      chartType: "bar",
      labels: ["H1 Business (72.5¢)", "H2 Business (76.0¢)", "Medical/Moving", "Charity (14.0¢)"],
      datasets: [{
        label: "Deduction ($)",
        data: [
          results.h1BusinessDeduction || 0,
          results.h2BusinessDeduction || 0,
          results.medicalMovingDeduction || 0,
          results.charityDeduction || 0
        ],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary, CHART_COLORS.blue, CHART_COLORS.accentOrange],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "13th-ahv-pension-calculator-2026") {
    return {
      chartType: "bar",
      labels: ["12 Regular Monthly Payments (CHF)", "December 13th Payout (CHF)"],
      datasets: [{
        label: "Annual Pension Breakdown",
        data: [
          results.regularAnnualPension || 0,
          results.december13thBonus || 0
        ],
        backgroundColor: [CHART_COLORS.blue, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "student-loan-save-transition-calculator-2026") {
    return {
      chartType: "bar",
      labels: ["Terminated SAVE", "New RAP (July 2026)", "Legacy IBR (10%)", "Standard 10-Yr"],
      datasets: [{
        label: "Monthly Payment ($)",
        data: [
          results.oldSaveMonthlyPayment || 0,
          results.rapMonthlyPayment || 0,
          results.newIdrMonthlyPayment || 0,
          results.standard10YrMonthlyPayment || 0
        ],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary, CHART_COLORS.blue, CHART_COLORS.danger],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "hra-exemption-calculator-8-cities") {
    return {
      chartType: "bar",
      labels: ["Exempt HRA (Tax-Free)", "Taxable HRA Portion", "Annual Tax Savings"],
      datasets: [{
        label: "HRA Breakdown (₹)",
        data: [
          results.exemptHraAnnual || 0,
          results.taxableHraAnnual || 0,
          results.annualTaxSavings || 0
        ],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger, CHART_COLORS.blue],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "401k-contribution-calculator-2026") {
    return {
      chartType: "bar",
      labels: ["Base Employee Deferral", "Age 50+ Catch-Up", "Employer Match"],
      datasets: [{
        label: "Annual 401(k) Funding ($)",
        data: [
          results.baseLimit || 0,
          results.catchUpLimit || 0,
          results.employerMatchDollars || 0
        ],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.blue, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "cpf-retirement-age-transition-calculator-2026") {
    return {
      chartType: "bar",
      labels: ["RA Base Interest (4.0%)", "Extra Interest Bonus", "1-Yr Compounding Bonus"],
      datasets: [{
        label: "CPF Interest Earned (S$)",
        data: [
          results.annualBaseInterestRA || 0,
          results.extraInterestBonus || 0,
          results.oneYearCompoundingBonus || 0
        ],
        backgroundColor: [CHART_COLORS.blue, CHART_COLORS.primary, CHART_COLORS.secondary],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "stage-3-tax-cut-15-percent-bracket-calculator-2026") {
    return {
      chartType: "bar",
      labels: ["15% Rate Cut Savings", "Instant Write-Off Tax Savings", "Total Combined Savings"],
      datasets: [{
        label: "Tax Savings ($)",
        data: [
          results.rateCutSavings || 0,
          results.writeOffTaxSavings || 0,
          results.totalCombinedSavings || 0
        ],
        backgroundColor: [CHART_COLORS.blue, CHART_COLORS.primary, CHART_COLORS.secondary],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "rbi-mis-selling-compensation-calculator-2026") {
    return {
      chartType: "bar",
      labels: ["Forced Insurance Refund", "Hidden Fees Refund", "Statutory Interest Penalty"],
      datasets: [{
        label: "Compensation Claim (₹)",
        data: [
          results.forcedInsurancePremium || 0,
          results.hiddenProcessingFees || 0,
          results.statutoryInterestPenalty || 0
        ],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.blue, CHART_COLORS.accentOrange],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "osfi-mortgage-stress-test-exemption-switch-calculator-2026") {
    return {
      chartType: "bar",
      labels: ["Contract Rate Payment", "Stress Test Rate Payment (+2%)", "Min Income Exempt Switch", "Min Income Full Stress Test"],
      datasets: [{
        label: "Mortgage Qualification Comparison ($)",
        data: [
          results.qualifyingMonthlyPaymentExempt || 0,
          results.qualifyingMonthlyPaymentFull || 0,
          results.minIncomeExemptSwitch || 0,
          results.minIncomeFullStressTest || 0
        ],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger, CHART_COLORS.blue, CHART_COLORS.secondary],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "irs-afr-intra-family-loan-calculator-august-2026") {
    return {
      chartType: "bar",
      labels: ["Family Rate Annual Interest", "IRS AFR Required Interest", "Annual Imputed Gift"],
      datasets: [{
        label: "Interest & Imputed Gift Comparison ($)",
        data: [
          results.annualFamilyInterest || 0,
          results.annualAfrInterest || 0,
          results.annualImputedGift || 0
        ],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger, CHART_COLORS.accentOrange],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "fair-work-minimum-wage-increase-calculator-july-2026") {
    return {
      chartType: "bar",
      labels: ["Weekly Gross Increase", "Annual Gross Increase", "Annual Super Boost", "Net Weekly Increase"],
      datasets: [{
        label: "Wage Increase Impact ($)",
        data: [
          results.weeklyGrossIncrease || 0,
          results.annualGrossIncrease || 0,
          results.annualSuperBoost || 0,
          results.netTakeHomeWeeklyIncrease || 0
        ],
        backgroundColor: [CHART_COLORS.blue, CHART_COLORS.secondary, CHART_COLORS.primary, CHART_COLORS.cyanDeep],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "fma-cccfa-complaint-eligibility-checker-july-2026") {
    return {
      chartType: "bar",
      labels: ["Interest actually paid", "Fees actually paid"],
      datasets: [{
        label: "Charges on your statements, not an estimated refund (NZ$)",
        data: [
          results.documentedInterestPaid || 0,
          results.documentedFeesPaid || 0
        ],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.blue],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "nz-fees-free-2026-completion-cutoff-calculator") {
    return {
      chartType: "bar",
      labels: ["If completed by cutoff", "At selected completion date", "Support lost after cutoff"],
      datasets: [{
        label: "Conditional Fees Free support (NZ$)",
        data: [results.supportIfOnTime || 0, results.supportUnderSelectedTiming || 0, results.amountLostAfterCutoff || 0],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.cyanDeep, CHART_COLORS.accentOrange],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "au-pension-supplement-overseas-12-week-calculator") {
    return {
      chartType: "bar",
      labels: ["Days with supplement", "Days without supplement"],
      datasets: [{
        label: "Days abroad",
        data: [results.payableDays || 0, results.unpaidDays || 0],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.accentOrange],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "california-ab1847-wildfire-forbearance-planner") {
    return {
      chartType: "bar",
      labels: ["Prior counted months", "Proposed months within cap", "Cap room afterward", "Requested months beyond cap"],
      datasets: [{
        label: "Forbearance planning months",
        data: [
          results.priorCountedMonths || 0,
          results.monthsWithinCap || 0,
          results.remainingAfterRequest || 0,
          results.monthsBeyondCap || 0
        ],
        backgroundColor: [CHART_COLORS.blue, CHART_COLORS.primary, CHART_COLORS.cyanDeep, CHART_COLORS.accentOrange],
        borderWidth: 1
      }]
    };
  }



  if (config.id === "altersvorsorgedepot-calculator-2027") {
    return {
      chartType: "bar",
      labels: ["Grundförderung (Base)", "Kinderzulage (Child)", "Total Investor Contributions", "Total State Subsidies"],
      datasets: [{
        label: "Altersvorsorgedepot Funding (€)",
        data: [
          results.annualBaseSubsidy || 0,
          results.annualChildSubsidy || 0,
          results.totalInvestorContributions || 0,
          results.totalStateSubsidies || 0
        ],
        backgroundColor: [CHART_COLORS.blue, CHART_COLORS.cyanDeep, CHART_COLORS.secondary, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // Age-based or Year-based projection
  if (results.projection) {
    const isYear = !!results.useYearLabel;
    const labelKey = isYear ? "yearLabel" : "ageLabel";
    return createBalanceContributionsDataset(
      results.projection.map((d: any) => ({
        ...d,
        [labelKey]: isYear ? `Year ${d.age}` : `Age ${d.age}`
      })),
      labelKey,
      "savings",
      "contributions"
    );
  }

  // FIRE log
  if (results.fireLog) {
    const labels = results.fireLog.map((d: any) => `Yr ${d.year}`);
    const portfolioData = results.fireLog.map((d: any) => d.portfolioValue);
    const targetData = results.fireLog.map((d: any) => d.targetCorpus);

    return {
      labels,
      datasets: [
        {
          label: "Portfolio Value",
          data: portfolioData,
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_FILLS.primarySoft,
          tension: 0.2,
          fill: true,
        },
        {
          label: "Target Corpus (Inflation Adj)",
          data: targetData,
          borderColor: CHART_COLORS.danger,
          backgroundColor: "transparent",
          borderDash: [5, 5],
          tension: 0.2,
        },
      ],
    };
  }

  // Schedule data (SWR or Mortgage)
  if (results.schedule) {
    const first = results.schedule[0];
    if (first && first.withdrawal !== undefined) {
      // SWR schedule
      const labels = results.schedule.map((d: any) => `Yr ${d.year}`);
      const corpusData = results.schedule.map((d: any) => d.remainingCorpus);
      return {
        labels,
        datasets: [
          {
            label: "Remaining Corpus",
            data: corpusData,
            borderColor: CHART_COLORS.primary,
            backgroundColor: CHART_FILLS.primarySoft,
            tension: 0.2,
            fill: true,
          },
        ],
      };
    } else if (first && first.balance !== undefined) {
      // Mortgage amortization schedule
      const labels = results.schedule.map((d: any) => `${d.period}`);
      const balanceData = results.schedule.map((d: any) => d.balance);
      return {
        labels,
        datasets: [
          {
            label: "Remaining Loan Balance",
            data: balanceData,
            borderColor: CHART_COLORS.danger,
            backgroundColor: CHART_FILLS.dangerSoft,
            tension: 0.2,
            fill: true,
          },
        ],
      };
    }
  }


  // Tax calculator doughnut
  if ((config.category === "tax" || config.id === "hecs-help") && (results.takeHome !== undefined || results.takeHomePay !== undefined)) {
    const takeHome = results.takeHome ?? results.takeHomePay;
    const taxes = results.totalTax ?? (results.grossIncome - takeHome);
    return {
      labels: ["Take-Home Pay", "Taxes & Deductions"],
      datasets: [
        {
          data: [
            takeHome,
            taxes,
          ],
          backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger],
          borderColor: CHART_FILLS.neutralSoft,
          borderWidth: 1,
        },
      ],
    };
  }

  // 50-30-20 budget
  if (config.id === "50-30-20" && results.needs !== undefined) {
    return {
      labels: ["Needs (50%)", "Wants (30%)", "Savings (20%)"],
      datasets: [
        {
          data: [results.needs, results.wants, results.savings],
          backgroundColor: [CHART_COLORS.danger, CHART_COLORS.accentOrange, CHART_COLORS.primary],
          borderColor: CHART_FILLS.neutralSoft,
          borderWidth: 1,
        },
      ],
    };
  }

  // Mortgage principal vs interest
  if (config.id === "mortgage" && results.totalInterest !== undefined && inputs.principal) {
    return {
      labels: ["Principal Loan Amount", "Total Interest Paid"],
      datasets: [
        {
          data: [inputs.principal, results.totalInterest],
          backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.accentOrange],
          borderColor: CHART_FILLS.neutralSoft,
          borderWidth: 1,
        },
      ],
    };
  }

  // DCA log chart data
  if (results.dcaLog) {
    const labels = results.dcaLog.map((d: any) => d.date);
    const valueData = results.dcaLog.map((d: any) => d.portfolioValue);
    const investedData = results.dcaLog.map((d: any) => d.totalInvested);

    return {
      labels,
      datasets: [
        {
          label: "Portfolio Value",
          data: valueData,
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_FILLS.primarySoft,
          tension: 0.2,
          fill: true,
        },
        {
          label: "Total Invested",
          data: investedData,
          borderColor: CHART_COLORS.secondary,
          backgroundColor: "transparent",
          tension: 0.2,
        },
      ],
    };
  }

  // Debt Payoff curve
  if (results.payoffSchedule) {
    const labels = results.payoffSchedule.map((d: any) => `Mo ${d.month}`);
    const totalRemainingData = results.payoffSchedule.map((d: any) => {
      return d.debtsPaidThisMonth.reduce((sum: number, r: any) => sum + r.remainingBalance, 0);
    });

    return {
      labels,
      datasets: [
        {
          label: "Total Remaining Debt",
          data: totalRemainingData,
          borderColor: CHART_COLORS.danger,
          backgroundColor: CHART_FILLS.dangerSoft,
          tension: 0.2,
          fill: true,
        }
      ]
    };
  }

  // Mortgage Affordability Budget
  if (config.id === "mortgage-affordability" && results.maxLoanAmount !== undefined && inputs.downPayment !== undefined) {
    return {
      labels: ["Mortgage Loan Portion", "Down Payment Portion"],
      datasets: [{
        data: [results.maxLoanAmount, inputs.downPayment],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Mortgage Overpayment curve (comparative)
  if (config.id === "mortgage-overpayment" && inputs.principal !== undefined && inputs.annualRate !== undefined && inputs.termYears !== undefined) {
    const principal = Number(inputs.principal);
    const annualRate = Number(inputs.annualRate);
    const termYears = Number(inputs.termYears);
    const extraPayment = Number(inputs.extraMonthlyPayment ?? 0);
    const monthlyRate = annualRate / 100 / 12;
    const n = termYears * 12;
    const normalPayment = (principal * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    
    const standardBalances = [];
    const overpaymentBalances = [];
    const years = [];
    let standardBalance = principal;
    let overpaymentBalance = principal;
    
    for (let year = 1; year <= termYears; year++) {
      years.push(`Yr ${year}`);
      for (let m = 0; m < 12; m++) {
        const interest = standardBalance * monthlyRate;
        const pPaid = normalPayment - interest;
        standardBalance = Math.max(0, standardBalance - pPaid);
      }
      standardBalances.push(Math.round(standardBalance));
      
      for (let m = 0; m < 12; m++) {
        const interest = overpaymentBalance * monthlyRate;
        const pPaid = (normalPayment + extraPayment) - interest;
        overpaymentBalance = Math.max(0, overpaymentBalance - pPaid);
      }
      overpaymentBalances.push(Math.round(overpaymentBalance));
    }
    
    return {
      labels: years,
      datasets: [
        {
          label: "Standard Amortization",
          data: standardBalances,
          borderColor: CHART_COLORS.danger,
          backgroundColor: "transparent",
          tension: 0.2,
        },
        {
          label: "With Overpayments",
          data: overpaymentBalances,
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_FILLS.primarySoft,
          tension: 0.2,
          fill: true,
        }
      ]
    };
  }

  // Rent vs Buy comparison
  if (config.id === "rent-vs-buy" && results.totalCostBuying !== undefined && results.totalCostRenting !== undefined) {
    return {
      labels: ["Buying Cost", "Renting Cost"],
      datasets: [{
        label: "Total Projected Cost",
        data: [Math.round(results.totalCostBuying), Math.round(results.totalCostRenting)],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.accentOrange],
        borderWidth: 1
      }]
    };
  }

  // Refinance savings
  if (config.id === "refinance" && results.currentMonthlyPayment !== undefined && results.newMonthlyPayment !== undefined) {
    return {
      labels: ["Current Monthly Payment", "Refinanced Monthly Payment"],
      datasets: [{
        label: "Monthly Outflow",
        data: [Math.round(results.currentMonthlyPayment), Math.round(results.newMonthlyPayment)],
        backgroundColor: [CHART_COLORS.danger, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // Down Payment goal progress
  if (config.id === "down-payment" && inputs.targetPrice !== undefined && inputs.downPercent !== undefined && inputs.currentSavings !== undefined) {
    const targetPrice = Number(inputs.targetPrice);
    const downPercent = Number(inputs.downPercent);
    const target = targetPrice * (downPercent / 100);
    const current = Number(inputs.currentSavings);
    if (target > current) {
      return {
        labels: ["Current Savings", "Remaining Gap to Target"],
        datasets: [{
          data: [current, target - current],
          backgroundColor: [CHART_COLORS.primary, CHART_COLORS.grey],
          borderColor: CHART_FILLS.neutralSoft,
          borderWidth: 1
        }]
      };
    }
  }

  // Budgeting DTI ratios
  if (config.id === "dti" && results.dtiRatio !== undefined) {
    return {
      labels: ["Your DTI Ratio", "Healthy Limit Target", "Maximum Debt Cap"],
      datasets: [{
        label: "DTI Ratio (%)",
        data: [results.dtiRatio, 36, 43],
        backgroundColor: [results.dtiRatio > 43 ? CHART_COLORS.danger : results.dtiRatio > 36 ? CHART_COLORS.accentOrange : CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.slate],
        borderWidth: 1
      }]
    };
  }

  // APRA DTI check gauge
  if (config.id === "apra-dti-cap-check" && results.dtiRatio !== undefined) {
    return {
      labels: ["Your DTI Ratio", "APRA Standard Limit", "APRA High-Risk Cap"],
      datasets: [{
        label: "DTI Multiple (x)",
        data: [results.dtiRatio, 4.0, 6.0],
        backgroundColor: [results.dtiRatio >= 6.0 ? CHART_COLORS.danger : results.dtiRatio >= 4.0 ? CHART_COLORS.accentOrange : CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.slate],
        borderWidth: 1
      }]
    };
  }

  // Net Worth distribution
  if (config.id === "net-worth" && inputs.assets !== undefined && inputs.liabilities !== undefined) {
    return {
      labels: ["Total Assets", "Total Liabilities"],
      datasets: [{
        data: [inputs.assets, inputs.liabilities],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Emergency Fund buffers
  if (config.id === "emergency-fund" && results.threeMonthBuffer !== undefined) {
    return {
      labels: ["3-Month Buffer", "6-Month Buffer", "Your Chosen Target"],
      datasets: [{
        label: "Emergency Reserves",
        data: [results.threeMonthBuffer, results.sixMonthBuffer, results.targetAmount],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.accentOrange, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // Savings Goal details
  if (config.id === "goal" && results.totalPrincipal !== undefined && results.totalInterestEarned !== undefined) {
    return {
      labels: ["Principal Deposited", "Compound Interest Earned"],
      datasets: [{
        data: [results.totalPrincipal, results.totalInterestEarned],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.accentOrange],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Inflation impact decay
  if (config.id === "inflation-impact" && results.futurePurchasingPower !== undefined && results.lossInValue !== undefined) {
    return {
      labels: ["Future Purchasing Power", "Inflation Value Loss"],
      datasets: [{
        data: [Math.round(results.futurePurchasingPower), Math.round(results.lossInValue)],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Real vs Nominal return yields
  if (config.id === "real-return" && results.realRate !== undefined && inputs.nominalRate !== undefined) {
    return {
      labels: ["Nominal Yield", "Real Yield (Inflation Adj)"],
      datasets: [{
        label: "Return Rate (%)",
        data: [Number(inputs.nominalRate), Number(results.realRate)],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // Fixed Deposit CD earnings
  if (config.id === "fixed-deposit" && results.interestEarned !== undefined && inputs.principal !== undefined) {
    return {
      labels: ["Principal Deposited", "CD Interest Growth"],
      datasets: [{
        data: [Number(inputs.principal), results.interestEarned],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.accentOrange],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Investment net capital gains
  if (config.id === "investment-returns" && results.netGain !== undefined && inputs.principal !== undefined) {
    return {
      labels: ["Initial Principal", "Net Capital Gain"],
      datasets: [{
        data: [Number(inputs.principal), results.netGain],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Premium bonds expected annual returns
  if (config.id === "premium-bonds-expected-return" && results.expectedReturn !== undefined && inputs.investmentAmount !== undefined) {
    return {
      labels: ["Bond Holdings", "Expected Annual Returns"],
      datasets: [{
        data: [Number(inputs.investmentAmount), results.expectedReturn],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Retirement Corpus Needed
  if (config.id === "corpus-needed" && results.corpus !== undefined && inputs.monthlyExpense !== undefined) {
    return {
      labels: ["Annual Income Target", "Required Nest Egg Corpus"],
      datasets: [{
        label: "Target Value",
        data: [Number(inputs.monthlyExpense) * 12, results.corpus],
        backgroundColor: [CHART_COLORS.accentOrange, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // Annual pensions vs current salaries
  if ((config.id === "pension" || config.id === "social-security") && results.annualPension !== undefined && inputs.principal !== undefined) {
    return {
      labels: ["Final Salary/Average Income", "Projected Annual Pension Benefit"],
      datasets: [{
        label: "Annual Compensation",
        data: [Number(inputs.principal), results.annualPension],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // Payday super compliance outflows
  if (config.id === "payday-super-compliance" && results.quarterlyOutflow !== undefined && results.cycleOutflow !== undefined) {
    return {
      labels: ["Standard Quarterly Payment", "Required Payday Payment"],
      datasets: [{
        label: "Payment Outflow",
        data: [results.quarterlyOutflow, results.cycleOutflow],
        backgroundColor: [CHART_COLORS.danger, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // CPF Budget top-up matching
  if (config.id === "budget-2026-cpf-top-up-checker" && results.topUpAmount !== undefined && inputs.cpfRetirementSavings !== undefined) {
    return {
      labels: ["Current CPF Balance", "Matched Top-Up Bonus"],
      datasets: [{
        label: "Singapore Dollars (S$)",
        data: [Number(inputs.cpfRetirementSavings), results.topUpAmount],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // Pillar 3a Retroactive buy-in values
  if (config.id === "pillar-3a-retroactive-buy-in" && results.totalGapVal !== undefined && results.totalTaxSavings !== undefined) {
    return {
      labels: ["Retroactive Buy-In Gap Value", "Estimated Tax Relief Savings"],
      datasets: [{
        data: [results.totalGapVal, results.totalTaxSavings],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Frühstart-Rente growth projections
  if (config.id === "fruehstart-rente-growth" && results.totalContributions !== undefined && results.totalInterestAt67 !== undefined) {
    return {
      labels: ["Total Principal Deposited", "Compound Interest Growth at 67"],
      datasets: [{
        data: [results.totalContributions, results.totalInterestAt67],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // UPS guaranteed monthly pension
  if (config.id === "ups-pension-estimator" && results.guaranteedPension !== undefined && results.familyPension !== undefined) {
    return {
      labels: ["Guaranteed Pension Benefit", "Guaranteed Family Pension"],
      datasets: [{
        label: "Monthly Benefit (₹)",
        data: [results.guaranteedPension, results.familyPension],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.secondary],
        borderWidth: 1
      }]
    };
  }

  // OBBBA Deductions breakdown
  if (config.id === "obbba-total-savings" && results.totalDeduction !== undefined) {
    return {
      labels: ["Tips Deduction", "Overtime Deduction", "Senior Deduction", "Auto Interest Deduction"],
      datasets: [{
        data: [
          results.tipsDeduction || 0,
          results.overtimeDeduction || 0,
          results.seniorDeduction || 0,
          results.autoInterestDeduction || 0
        ],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.accentOrange, CHART_COLORS.pink],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Capital Gains Net Proceeds
  if (config.id === "capital-gains" && results.netProceeds !== undefined && results.taxOwed !== undefined) {
    return {
      labels: ["Net Post-Tax Proceeds", "Capital Gains Tax Owed"],
      datasets: [{
        data: [results.netProceeds, results.taxOwed],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Box 3 wealth tax comparison
  if (config.id === "box3-wealth-tax" && results.tax2026 !== undefined && results.tax2028 !== undefined) {
    return {
      labels: ["2026 System Tax", "2028 System Tax"],
      datasets: [{
        label: "Wealth Tax (€)",
        data: [results.tax2026, results.tax2028],
        backgroundColor: [CHART_COLORS.danger, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // Swiss Aktivrente tax savings
  if (config.id === "aktivrente-tax-savings" && results.netIncomeWith !== undefined && results.netIncomeWithout !== undefined) {
    return {
      labels: ["Net Income (Without)", "Net Income (With)"],
      datasets: [{
        label: "Net Annual Income (€)",
        data: [results.netIncomeWithout, results.netIncomeWith],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // Indian LRS TCS tax collection
  if (config.id === "lrs-tcs-calculator" && results.netRemittance !== undefined && results.tcsAmount !== undefined) {
    return {
      labels: ["Net Remittance Sent", "TCS Tax Collected"],
      datasets: [{
        data: [results.netRemittance, results.tcsAmount],
        backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger],
        borderColor: CHART_FILLS.neutralSoft,
        borderWidth: 1
      }]
    };
  }

  // Instant Tax Deduction comparative bar chart
  if (config.id === "instant-deduction" && results.oldClaim !== undefined && results.newClaim !== undefined) {
    return {
      labels: ["Deduction under Old Rules", "Deduction under New Rules"],
      datasets: [{
        label: "Claimable Deduction (AUD)",
        data: [results.oldClaim, results.newClaim],
        backgroundColor: [CHART_COLORS.grey, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  // VA Partial Claim Estimator comparative bar chart
  if (config.id === "va-partial-claim-estimator" && results.estimatedClaimAmount !== undefined && results.remainingArrears !== undefined) {
    return {
      labels: ["Total Arrearage Owed", "Estimated VA Claim Amount", "Remaining Arrears After Claim"],
      datasets: [{
        label: "Amount ($)",
        data: [inputs.arrearageAmount ?? 15000, results.estimatedClaimAmount, results.remainingArrears],
        backgroundColor: [CHART_COLORS.danger, CHART_COLORS.primary, CHART_COLORS.accentOrange],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "offset-error-cost-calculator" && results.monthlyExtraInterest !== undefined && results.extraInterestPaid !== undefined) {
    // Escalation view: the same broken link costed out per month, per year,
    // and over the user's own unlinked window.
    const months = Number(inputs.monthsUnlinked) || 12;
    return {
      chartType: "bar",
      labels: ["Per month", "Per year", `Your ${months}-month total`],
      datasets: [{
        label: "Extra interest (A$)",
        data: [
          results.monthlyExtraInterest,
          results.monthlyExtraInterest * 12,
          results.extraInterestPaid
        ],
        backgroundColor: [CHART_COLORS.accentOrange, CHART_COLORS.danger, CHART_COLORS.primary],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "capital-one-360-settlement-payout-estimator" && results.recognizedClaimEstimate !== undefined) {
    // Claim vs the two pro-rata payout scenarios, plus the yearly value of
    // the forced rate matching for accounts still open.
    return {
      chartType: "bar",
      labels: ["Recognized claim", "Conservative payout", "Upper payout", "Rate matching / year"],
      datasets: [{
        label: "Amount ($)",
        data: [
          results.recognizedClaimEstimate,
          results.estimatedPayoutLow ?? 0,
          results.estimatedPayoutHigh ?? 0,
          results.annualFutureBenefit ?? 0
        ],
        backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.accentOrange, CHART_COLORS.primary, CHART_COLORS.accentCyan],
        borderWidth: 1
      }]
    };
  }

  if (config.id === "ssb-calculator" && Array.isArray(results.yearlyBreakdown)) {
    // Live step-up ladder for the exact holding period chosen: one rate per year.
    return {
      chartType: "line",
      labels: results.yearlyBreakdown.map((d: any) => `Year ${d.year}`),
      datasets: [{
        label: "Rate (% p.a.)",
        data: results.yearlyBreakdown.map((d: any) => d.rate),
        borderColor: CHART_COLORS.primary,
        backgroundColor: CHART_FILLS.primarySoft,
        tension: 0.2,
        fill: true
      }]
    };
  }

  // Generic deduction/tax savings doughnut fallback
  if (results.deduction !== undefined && results.deduction > 0) {
    const base = inputs.grossIncome || inputs.magi || inputs.tipIncome || inputs.overtimeIncome || inputs.autoInterestPaid || 0;
    if (base > results.deduction) {
      return {
        labels: ["Deductible Amount", "Taxable/Standard Portion"],
        datasets: [{
          data: [results.deduction, base - results.deduction],
          backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger],
          borderColor: CHART_FILLS.neutralSoft,
          borderWidth: 1
        }]
      };
    }
  }

  return null;
}

/**
 * Determines if the results contain projection data
 * and extracts the projection table configuration
 */
export function getProjectionTableData(
  results: Record<string, any>,
  config?: CalculatorConfig,
  inputs: Record<string, any> = {}
): {
  hasProjection: boolean;
  headers: string[];
  rows: Array<Array<string | number>>;
  title?: string;
} {
  let hasProjection = false;
  let projectionTitle: string | undefined = undefined;
  let projectionHeaders: string[] = [];
  let projectionRows: Array<Array<string | number>> = [];

  if (!results || Object.keys(results).length === 0) {
    return { hasProjection, headers: projectionHeaders, rows: projectionRows };
  }

  if (config?.id === "irs-mileage-rate-calculator-2026") {
    hasProjection = true;
    projectionTitle = "IRS Mid-Year Mileage Rate Split (H1 vs H2) Deduction Breakdown";
    projectionHeaders = ["Category / Half-Year", "Rate per Mile", "Deduction ($)", "Est. Tax Savings ($)"];
    projectionRows = [
      ["H1 Business (Jan 1 – Jun 30)", "72.5¢", (results.h1BusinessDeduction || 0).toLocaleString(), Math.round((results.h1BusinessDeduction || 0) * 0.24).toLocaleString()],
      ["H2 Business (Jul 1 – Dec 31)", "76.0¢", (results.h2BusinessDeduction || 0).toLocaleString(), Math.round((results.h2BusinessDeduction || 0) * 0.24).toLocaleString()],
      ["Medical & Moving Driving", "20.5¢ / 23.5¢", (results.medicalMovingDeduction || 0).toLocaleString(), Math.round((results.medicalMovingDeduction || 0) * 0.24).toLocaleString()],
      ["Charitable Driving", "14.0¢", (results.charityDeduction || 0).toLocaleString(), Math.round((results.charityDeduction || 0) * 0.24).toLocaleString()],
      ["Total All Mileage", "Blended", (results.totalMileageDeduction || 0).toLocaleString(), (results.estimatedTaxSavings || 0).toLocaleString()]
    ];
  } else if (config?.id === "13th-ahv-pension-calculator-2026") {
    hasProjection = true;
    projectionTitle = "13th AHV Pension (Switzerland) 10-Year Cumulative Projection";
    projectionHeaders = ["Year", "Regular Monthly Pension (CHF)", "12-Month Base (CHF)", "December 13th Bonus (CHF)", "Cumulative Total Pension Received (CHF)"];
    const m = results.monthlyAhvPension || 2520;
    const bonus = results.december13thBonus || 2520;
    const annual = results.totalAnnualAHV || 32760;
    projectionRows = Array.from({ length: 10 }, (_, i) => [
      `Year ${i + 1}`,
      m.toLocaleString(),
      (m * 12).toLocaleString(),
      bonus.toLocaleString(),
      (annual * (i + 1)).toLocaleString()
    ]);
  } else if (config?.id === "student-loan-save-transition-calculator-2026") {
    hasProjection = true;
    projectionTitle = "Student Loan SAVE Transition 10-Year Cumulative Payment Comparison";
    projectionHeaders = ["Year", "Terminated SAVE Cumulative ($)", "New RAP Cumulative ($)", "Legacy IBR Cumulative ($)", "Standard 10-Year Cumulative ($)"];
    const saveM = results.oldSaveMonthlyPayment || 0;
    const rapM = results.rapMonthlyPayment || 0;
    const ibrM = results.newIdrMonthlyPayment || 0;
    const stdM = results.standard10YrMonthlyPayment || 0;
    projectionRows = Array.from({ length: 10 }, (_, i) => [
      `Year ${i + 1}`,
      Math.round(saveM * 12 * (i + 1)).toLocaleString(),
      Math.round(rapM * 12 * (i + 1)).toLocaleString(),
      Math.round(ibrM * 12 * (i + 1)).toLocaleString(),
      Math.round(stdM * 12 * (i + 1)).toLocaleString()
    ]);
  } else if (config?.id === "hra-exemption-calculator-8-cities") {
    hasProjection = true;
    projectionTitle = "India 8-City HRA Exemption Statutory 3-Clause Evaluation";
    projectionHeaders = ["Statutory Exemption Clause", "Annual Calculation (₹)", "Allowed Exemption Status"];
    projectionRows = [
      ["1. Actual HRA Received from Employer", (results.actualHraAnnual || 0).toLocaleString(), "Base Upper Limit"],
      ["2. Rent Paid Minus 10% of Salary (Basic + DA)", (results.rentMinus10PercentSalary || 0).toLocaleString(), "Rent Exemption Floor"],
      ["3. City Limit (50% Notified 8-City Metro / 40% Non-Metro)", (results.cityLimitAmount || 0).toLocaleString(), "Notified Metro Rule Ceiling"],
      ["FINAL ALLOWABLE TAX-EXEMPT HRA", (results.exemptHraAnnual || 0).toLocaleString(), "Minimum of 3 Clauses (Tax-Free)"]
    ];
  } else if (config?.id === "401k-contribution-calculator-2026") {
    hasProjection = true;
    projectionTitle = "401(k) 10-Year Balance & Employer Match Growth Projection";
    projectionHeaders = ["Year (Age)", "Employee Base Deferral ($)", "Catch-Up ($)", "Employer Match ($)", "Total Annual Deposit ($)", "Projected Balance @ 7% ($)"];
    const baseD = results.baseLimit || 24500;
    const matchD = results.employerMatchDollars || 0;
    const startAge = Number(inputs?.age) || 45;
    let balance = 0;
    projectionRows = Array.from({ length: 10 }, (_, i) => {
      const projAge = startAge + i;
      let yrCatchUp = 0;
      if (projAge >= 60 && projAge <= 63) {
        yrCatchUp = 11250;
      } else if (projAge >= 50) {
        yrCatchUp = 8000;
      }
      const annualDep = baseD + yrCatchUp + matchD;
      balance = (balance + annualDep) * 1.07;
      return [
        `Year ${i + 1} (${projAge})`,
        baseD.toLocaleString(),
        yrCatchUp.toLocaleString(),
        matchD.toLocaleString(),
        annualDep.toLocaleString(),
        Math.round(balance).toLocaleString()
      ];
    });
  } else if (config?.id === "cpf-retirement-age-transition-calculator-2026") {
    hasProjection = true;
    projectionTitle = "Singapore CPF Retirement Account Interest & Payout Projection";
    projectionHeaders = ["Age Milestones", "Base RA Rate", "Bonus Tier", "Projected Monthly Payout (S$)"];
    const baseP = results.projectedMonthlyPayout || 1500;
    projectionRows = [
      ["Age 55 (Retirement Account Creation & SA Closure)", "4.0% Base RA", "+2.0% on First $30k, +1.0% on Next $30k (Up to 6% p.a.)", "S$ 0 (Accumulation Phase)"],
      ["Age 64 (Statutory Retirement Age)", "4.0% Base RA", "Re-Employment Eligible to Age 69", "S$ 0 (Accumulation Phase)"],
      ["Age 65 (Statutory CPF LIFE Payout Start)", "4.0% Base RA", "Tiered Extra Interest (Up to 6% p.a.)", `S$ ${baseP.toLocaleString()} / mo`],
      ["Age 70 (Maximum Deferred Payout)", "4.0% Base RA", "+7% Payout Enhancement / Year Deferred", `S$ ${Math.round(baseP * 1.35).toLocaleString()} / mo`]
    ];
  } else if (config?.id === "stage-3-tax-cut-15-percent-bracket-calculator-2026") {
    hasProjection = true;
    projectionTitle = "Australia Stage 3 Rate Cut & Instant Write-Off Savings Breakdown";
    projectionHeaders = ["Tax Provision / Bracket Shift", "Applicable Rate / Threshold", "Annual Tax Savings ($)"];
    projectionRows = [
      ["15% Lower Income Tax Bracket Cut", "$18,201 – $45,000 Bracket", (results.taxCutSavings15Percent || 0).toLocaleString()],
      ["$20,000 Instant Asset Write-Off Relief", "100% Upfront Depreciation", (results.writeOffTaxSavings || 0).toLocaleString()],
      ["Combined Annual Tax Relief Windfall", "Stage 3 + Write-Off Total", (results.totalCombinedSavings || 0).toLocaleString()]
    ];
  } else if (config?.id === "rbi-mis-selling-compensation-calculator-2026") {
    hasProjection = true;
    projectionTitle = "India RBI Anti-Mis-Selling Ombudsman Claim Breakdown";
    projectionHeaders = ["Claim Component", "Statutory Provision", "Refund / Compensation Amount (₹)"];
    projectionRows = [
      ["Forced Loan Insurance Premium", "Full Refund Mandate", (results.forcedInsurancePremium || 0).toLocaleString()],
      ["Hidden & Unauthorized Processing Fees", "100% Fee Reversal", (results.hiddenProcessingFees || 0).toLocaleString()],
      ["Statutory 6% Interest Penalty for Delay", "RBI Ombudsman Interest", (results.statutoryInterestPenalty || 0).toLocaleString()],
      ["TOTAL ELIGIBLE OMBUDSMAN CLAIM", "Full Legal Compensation", (results.totalCompensationClaim || 0).toLocaleString()]
    ];
  } else if (config?.id === "osfi-mortgage-stress-test-exemption-switch-calculator-2026") {
    hasProjection = true;
    projectionTitle = "Canada OSFI Mortgage Switch Qualification & Stress Test Exemption Comparison";
    projectionHeaders = ["Assessment Criteria", "Contract Rate Switch (Exempt)", "Full Stress Test (+2.0%)", "Switch Advantage / Savings"];
    const mExempt = results.qualifyingMonthlyPaymentExempt || 0;
    const mFull = results.qualifyingMonthlyPaymentFull || 0;
    const incExempt = results.minIncomeExemptSwitch || 0;
    const incFull = results.minIncomeFullStressTest || 0;
    projectionRows = [
      ["Qualifying Monthly Mortgage Payment", `$${mExempt.toLocaleString()}`, `$${mFull.toLocaleString()}`, `-$${(mFull - mExempt).toLocaleString()} / mo`],
      ["Minimum Income Required to Qualify", `$${incExempt.toLocaleString()}`, `$${incFull.toLocaleString()}`, `$${(incFull - incExempt).toLocaleString()} Lower Threshold`]
    ];
  } else if (config?.id === "irs-afr-intra-family-loan-calculator-august-2026") {
    const term = Math.min(30, Math.max(1, inputs.loanTermYears || 5));
    hasProjection = true;
    projectionTitle = `IRS August 2026 AFR Intra-Family Loan ${term}-Year Schedule`;
    projectionHeaders = ["Year", "Family Interest Paid ($)", "Required AFR Interest ($)", "Annual Imputed Gift ($)", `${term}-Year Cumulative Imputed Gift ($)`];
    const famInt = results.annualInterestCharged || 0;
    const afrInt = results.annualRequiredAfrInterest || 0;
    const gift = results.annualImputedInterest || 0;
    projectionRows = Array.from({ length: term }, (_, i) => [
      `Year ${i + 1}`,
      famInt.toLocaleString(),
      afrInt.toLocaleString(),
      gift.toLocaleString(),
      Math.round(gift * (i + 1)).toLocaleString()
    ]);
  } else if (config?.id === "fair-work-minimum-wage-increase-calculator-july-2026") {
    hasProjection = true;
    projectionTitle = "Australia Fair Work 4.75% Minimum Wage Increase Comparison";
    projectionHeaders = ["Pay Period", "Pre-July 1, 2026 Base Rate", "Post-July 1, 2026 (+4.75%)", "Gross Salary Increase"];
    const wBase = results.weeklyGrossBase || 915;
    const wNew = results.weeklyGrossNew || 958.46;
    projectionRows = [
      ["Weekly Base Rate", `$${wBase.toLocaleString()}`, `$${wNew.toLocaleString()}`, `+$${(wNew - wBase).toFixed(2)} / wk`],
      ["Annual Base Salary (52 wks)", `$${Math.round(wBase * 52).toLocaleString()}`, `$${Math.round(wNew * 52).toLocaleString()}`, `+$${(results.annualGrossIncrease || 0).toLocaleString()} / yr`],
      ["Superannuation Guarantee (12%)", `$${Math.round(wBase * 52 * 0.12).toLocaleString()}`, `$${Math.round(wNew * 52 * 0.12).toLocaleString()}`, `+$${(Math.round(wNew * 52 * 0.12) - Math.round(wBase * 52 * 0.12)).toLocaleString()} Super / yr`]
    ];
  } else if (config?.id === "fma-cccfa-complaint-eligibility-checker-july-2026") {
    hasProjection = true;
    projectionTitle = "NZ FMA CCCFA Responsible Lending Dispute Claim Breakdown";
    projectionHeaders = ["Violation Category", "CCCFA Statutory Rule", "Refund & Penalty Claim ($)"];
    projectionRows = [
      ["Unreasonable Lender Administration Fees", "Section 44 Mandatory Fee Refund", (results.unreasonableFeesRefund || 0).toLocaleString()],
      ["Unaffordable Credit Agreement Damages", "Section 9C Responsible Lending Breach", (results.affordabilityViolationDamages || 0).toLocaleString()],
      ["FMA Statutory Complaint Penalty", "Statutory Dispute Compensation", (results.statutoryPenaltyClaim || 0).toLocaleString()],
      ["TOTAL CCCFA COMPLAINT CLAIM", "Full Legal Redress", (results.totalCccfaClaimAmount || 0).toLocaleString()]
    ];

  } else if (config?.id === "altersvorsorgedepot-calculator-2027") {
    hasProjection = true;
    projectionTitle = "Germany Altersvorsorgedepot 30-Year Compounding Growth Projection";
    projectionHeaders = ["Year", "Annual Investor Contribution (€)", "State Grundförderung (€)", "Kinderzulage (€)", "Projected Portfolio (€)"];
    const inv = results.annualContribution || (inputs.annualContribution ?? 1800);
    const baseSub = results.totalBaseSubsidy || 0;
    const childSub = results.totalChildSubsidy || 0;
    const r = (inputs.expectedEtfReturn ?? 7.0) / 100;
    let portfolio = results.youngBonus || 0;
    projectionRows = Array.from({ length: 30 }, (_, i) => {
      portfolio = (portfolio + inv + baseSub + childSub) * (1 + r);
      return [
        `Year ${i + 1}`,
        `€ ${inv.toLocaleString()}`,
        `€ ${baseSub.toLocaleString()}`,
        `€ ${childSub.toLocaleString()}`,
        `€ ${Math.round(portfolio).toLocaleString()}`
      ];
    });
  } else if (results.yearlyBreakdown) {
    hasProjection = true;
    const isSsb = !!results.isSsb;
    projectionHeaders = isSsb
      ? ["Year", "Principal Held", "Cumulative Interest Paid", "Total Wealth (Principal + Interest)"]
      : ["Year", "Total Contributions", "Total Interest/Growth", "Ending Balance"];
    projectionRows = results.yearlyBreakdown.map((d: any) => [
      `Year ${d.year}`,
      d.contributions,
      d.interest,
      d.balance,
    ]);
  } else if (results.useHbpRepaymentSchedule) {
    hasProjection = true;
    projectionHeaders = ["Year", "Annual Repayment ($)", "Total Repaid to RRSP ($)", "Remaining HBP Balance ($)"];
    projectionRows = results.projection.map((d: any) => [
      `Year ${d.year}`,
      d.annualRepayment,
      d.totalRepaid,
      d.remainingBalance,
    ]);
  } else if (results.projection && config?.id === "overseas-student-loan-repayment-calculator") {
    hasProjection = true;
    projectionHeaders = ["Year", "Starting Balance ($)", "Annual Interest ($)", "Total Payment ($)", "Ending Balance ($)"];
    projectionRows = results.projection.map((d: any) => [
      `Year ${d.year}`,
      d.year === 1 ? (d.balance + d.repayment - d.interest).toFixed(2) : results.projection[d.year - 2]?.balance.toFixed(2) || "0.00",
      d.interest.toFixed(2),
      d.repayment.toFixed(2),
      d.balance.toFixed(2),
    ]);
  } else if (results.projection && config?.id === "irs-penalty-relief-aep") {
    hasProjection = true;
    projectionHeaders = ["Month", "Standard IRS Penalty ($)", "AEP Penalty Waiver ($)", "Net Penalty Owed ($)"];
    projectionRows = results.projection.map((d: any) => [
      `Month ${d.month}`,
      d.standardPenalty,
      d.aepRelief,
      d.netOwed,
    ]);
  } else if (results.projection && config?.id === "uk-bnpl-dpc-rights") {
    hasProjection = true;
    projectionHeaders = ["Month", "Scheduled Repayment (£)", "Missed Penalty / Late Fees (£)", "Cumulative Paid (£)", "Remaining Balance (£)"];
    projectionRows = results.projection.map((d: any) => [
      `Month ${d.month}`,
      d.scheduledPayment,
      d.lateFees,
      d.cumulativePaid,
      d.remainingBalance,
    ]);
  } else if (results.projection) {
    hasProjection = true;
    const isYear = !!results.useYearLabel;
    projectionHeaders = [isYear ? "Year" : "Age", "Total Contributions", "Total Interest/Growth", "Ending Balance"];
    projectionRows = results.projection.map((d: any) => [
      isYear ? `Year ${d.age}` : `Age ${d.age}`,
      d.contributions,
      d.interest,
      d.savings,
    ]);
  } else if (results.fireLog) {
    hasProjection = true;
    projectionHeaders = ["Year", "Portfolio Value", "Target Corpus (Inflation Adj.)", "FIRE Reached"];
    projectionRows = results.fireLog.map((d: any) => [
      `Year ${d.year}`,
      d.portfolioValue,
      d.targetCorpus,
      d.isFireReached ? "Yes" : "No",
    ]);
  } else if (results.schedule) {
    hasProjection = true;
    const first = results.schedule[0];
    if (first && first.withdrawal !== undefined) {
      // SWR
      projectionHeaders = ["Year", "Annual Withdrawal", "Remaining Corpus"];
      projectionRows = results.schedule.map((d: any) => [
        `Year ${d.year}`,
        d.withdrawal,
        d.remainingCorpus,
      ]);
    } else if (first && first.balance !== undefined) {
      // Mortgage
      projectionHeaders = ["Period", "Payment", "Principal Paid", "Interest Paid", "Remaining Balance"];
      projectionRows = results.schedule.map((d: any) => [
        `Period ${d.period}`,
        d.payment,
        d.principal,
        d.interest,
        d.balance,
      ]);
    }

  } else if (results.dcaLog) {
    hasProjection = true;
    projectionHeaders = ["Month", "Stock Price", "Amount Invested", "Total Invested", "Portfolio Value"];
    projectionRows = results.dcaLog.map((d: any) => [
      d.date,
      d.price,
      d.amountInvested,
      d.totalInvested,
      d.portfolioValue,
    ]);
  } else if (results.payoffSchedule) {
    hasProjection = true;
    projectionHeaders = ["Month", "Monthly Payment ($)", "Remaining Debt ($)"];
    projectionRows = results.payoffSchedule.map((d: any) => [
      `Month ${d.month}`,
      d.totalPayment,
      d.debtsPaidThisMonth.reduce((sum: number, r: any) => sum + r.remainingBalance, 0)
    ]);
  } else if (config?.id === "mortgage-overpayment" && results.newTermMonths !== undefined) {
    hasProjection = true;
    projectionHeaders = ["Year", "Standard Projected Balance ($)", "With Overpayments Balance ($)"];
    
    const principal = Number(inputs.principal);
    const annualRate = Number(inputs.annualRate);
    const termYears = Number(inputs.termYears);
    const extraPayment = Number(inputs.extraMonthlyPayment ?? 0);
    const monthlyRate = annualRate / 100 / 12;
    const n = termYears * 12;
    const normalPayment = (principal * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    
    let standardBalance = principal;
    let overpaymentBalance = principal;
    
    for (let year = 1; year <= termYears; year++) {
      for (let m = 0; m < 12; m++) {
        const interest = standardBalance * monthlyRate;
        const pPaid = normalPayment - interest;
        standardBalance = Math.max(0, standardBalance - pPaid);
      }
      for (let m = 0; m < 12; m++) {
        const interest = overpaymentBalance * monthlyRate;
        const pPaid = (normalPayment + extraPayment) - interest;
        overpaymentBalance = Math.max(0, overpaymentBalance - pPaid);
      }
      
      projectionRows.push([
        `Year ${year}`,
        Math.round(standardBalance),
        Math.round(overpaymentBalance)
      ]);
    }
  }

  return { hasProjection, headers: projectionHeaders, rows: projectionRows, title: projectionTitle };
}
